import React, {useCallback, useState} from "react";
import {
    DndContext,
    DragEndEvent, DragOverlay,
    DragStartEvent,
    PointerSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {DroppableCompSlot} from "./DroppableCompSlot.tsx";
import {DraggableCompRow} from "./DraggableCompRow.tsx";
import {
    COLUMN_CONFIGS,
    CompChangedResponse,
    customCollisionDetection,
    getAlignmentClass, SelectableComp, SelectedComp
} from "./configurableCompsSelectTypes.tsx";
import {CompSelectType, ConfigurableCompsSelectComponent} from "../../DynamicFormTypes.ts";
import {isCompTypeActive} from "../../dyanimcFormUtil.tsx";
import {AddComparableModal} from "./AddComparableModal.tsx";
import {Subject} from "../../../../api/ApiTypes.ts";

interface DragDropTableProps {
    selectableComps: SelectableComp[];
    subject?: Subject;
    selectedComps: SelectedComp[];
    setSelectedCompAtRow: (comp: SelectableComp, slot: SelectedComp) => Promise<CompChangedResponse>;
    componentDefinition: ConfigurableCompsSelectComponent
    getSelectedCompByMlsIdOrAddress: (address: string | undefined, mlsId: string | undefined) => SelectedComp | undefined;
    handleClickComp: (comp: SelectableComp) => void;
    handleRemoveComp: (selected: SelectedComp) => void;
}
export const SELECTABLE_TYPE = 'SELECTABLE_TYPE';
export const SELECTED_TYPE = 'SELECTED_TYPE';
//requires pointer to be withing droppable area
export const CompsDragDropTableRenderer: React.FC<DragDropTableProps> = ({
                                                                             selectableComps,
                                                                             subject,
                                                                             selectedComps,
                                                                             setSelectedCompAtRow,
                                                                             componentDefinition,
                                                                             getSelectedCompByMlsIdOrAddress,
                                                                             handleClickComp,
                                                                             handleRemoveComp
                                                                            }) => {
    const [activeDragSelectable, setActiveDragSelectable] = useState<SelectableComp | null>(null);

    // Configure drag sensors with activation constraint
    // This prevents accidental drags when users are trying to select text
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 10,
            },
        })
    );


    const handleDragStart = useCallback((event: DragStartEvent) => {
        setActiveDragSelectable(event.active?.data?.current?.SELECTABLE_TYPE as SelectableComp | null);
    }, []);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || !active) {
            //if dropped outside any droppable area, reset active drag
            setActiveDragSelectable(null);
            return;
        }

        const selectable = active?.data?.current?.SELECTABLE_TYPE as SelectableComp | null;
        const selected = over?.data?.current?.SELECTED_TYPE as SelectedComp | null;
        if (!selectable || !selected) {
            //if either is null, reset active drag
            setActiveDragSelectable(null);
            return;
        }
        // Only handle dropping rows onto slots
        if (selectable.dgInfo.requiredType !== selected.dgInfo.requiredType) {
            setActiveDragSelectable(null);
            return;
        }

        setSelectedCompAtRow(selectable, selected);
        setActiveDragSelectable(null);
    }, [setSelectedCompAtRow]);



    const renderDragOverlay = () => {
        if (!activeDragSelectable) return null;

        const statusClass = isCompTypeActive(activeDragSelectable.propertyValuation?.compType)
            ? "bg-white border-green-500"
            : "bg-white border-blue-500";

        return (
            <div className={`border-2 rounded-lg shadow-xl p-2 lg:p-3 pointer-events-none ${statusClass}`}>
                <div className="flex justify-evenly items-center gap-2">
                    {COLUMN_CONFIGS.map((column) => (
                        <span
                            key={column.key}
                            className={`px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 ${getAlignmentClass(column.align)} ${column.minWidth || ''} ${column.className || ''}`}
                        >
                            {column.render(activeDragSelectable.propertyValuation, false)}
                        </span>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <>
            <DndContext
                sensors={sensors}
                collisionDetection={customCollisionDetection}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                <div className="space-y-4 lg:space-y-6">
                    {/* Selected Comparables Section */}
                    <SelectedComparablesSection
                        componentDefinition={componentDefinition}
                        selectedComps={selectedComps}
                        onRemove={handleRemoveComp}
                    />

                    {/* Available Comparables Table */}
                    <AvailableComparablesTable
                        selectedComps={selectedComps}
                        subject={subject}
                        selectableComps={selectableComps}
                        onCompClick={handleClickComp}
                        setSelectedCompAtRow={setSelectedCompAtRow}
                        getSelectedCompByMlsIdOrAddress={getSelectedCompByMlsIdOrAddress}
                    />
                </div>

                {/* Drag Overlay */}
                <DragOverlay>
                    {renderDragOverlay()}
                </DragOverlay>
            </DndContext>


        </>


    );
};

export const CompsDragDropTable = React.memo(CompsDragDropTableRenderer);


const SelectedComparablesSection: React.FC<{
    selectedComps: SelectedComp[];
    onRemove: (selectedComp: SelectedComp) => void;
    componentDefinition: ConfigurableCompsSelectComponent;
}> = React.memo(({selectedComps, onRemove}) => {
    const gridColsClass = {
        1: "grid-cols-1",
        2: "grid-cols-2",
        3: "grid-cols-3",
        4: "grid-cols-4",
        5: "grid-cols-5",
        6: "grid-cols-6",
    }[selectedComps.length] || "grid-cols-1";
    return (
        <div className="bg-white rounded-lg shadow-md p-4 lg:p-6">
            <div className="flex items-center justify-between mb-3 lg:mb-4">
                <h2 className="text-lg lg:text-xl font-bold text-gray-800">Selected Comparables</h2>
                <div className="text-xs lg:text-sm text-gray-500">
                    {selectedComps.filter(comp => comp && comp.address?.trim()).length} / {selectedComps.length} Selected
                </div>
            </div>

            <div className={`grid ${gridColsClass} gap-3 lg:gap-4 min-w-full`}>
                {selectedComps.map((comp, index) => (
                    <DroppableCompSlot
                        key={`slot-${index}`}
                        index={index}
                        selectedComp={comp}
                        onRemove={() => onRemove(comp)}
                    />
                ))}
            </div>
        </div>
    )
});

export interface SortingMethod {
    descending: boolean;
    label: SortingOption;
}

export enum SortingOption {
    RANKING = "ranking",
    MLSID = "mlsId",
    ADDRESS = "address",
    PROPERTY_TYPE = "propertyType",
    LAST_TRANSACTION_DATE = "lastTransactionDate",
    GARAGE = "garage",
    SALE_STATUS = "saleStatus",
    SALE_TYPE = "saleType",
    PROXIMITY = "proximityInMiles",
    LAST_TRANSACTION_PRICE = "lastTransactionPrice",
    ORIGINAL_PRICE = "originalPrice",
    DAYS_ON_MARKET = "daysOnMarket",
    ALL_ROOMS_COUNT = "allRoomsCount",
    BEDROOM_COUNT = "bedroomCount",
    FULL_BATHROOM_COUNT = "fullBathroomCount",
    STORIES = "stories",
    UNITS = "units",
    GROSS_LIVING_AREA = "grossLivingArea",
    YEAR_BUILT = "yearBuilt",
    LOT_SIZE = "lotSize",
}

const AvailableComparablesTable: React.FC<{
    subject?: Subject;
    selectableComps: SelectableComp[];
    onCompClick?: (selectableComp: SelectableComp) => void;
    selectedComps: SelectedComp[];
    setSelectedCompAtRow: (comp: SelectableComp, slot: SelectedComp) => Promise<CompChangedResponse>;
    getSelectedCompByMlsIdOrAddress: (address: string | undefined, mlsId: string | undefined) => SelectedComp | undefined;
}> = React.memo(({subject, selectableComps, onCompClick, selectedComps, setSelectedCompAtRow, getSelectedCompByMlsIdOrAddress}) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [currentSortingMethod, setCurrentSortingMethod] = React.useState<SortingMethod>({
        descending: false,
        label: SortingOption.RANKING
    });
    const sortAlphabetically = [
        SortingOption.MLSID,
        SortingOption.ADDRESS,
        SortingOption.PROPERTY_TYPE,
        SortingOption.LAST_TRANSACTION_DATE,
        SortingOption.GARAGE,
        SortingOption.SALE_STATUS,
        SortingOption.SALE_TYPE
    ];
    const sortNumerically = [
        SortingOption.PROXIMITY,
        SortingOption.LAST_TRANSACTION_PRICE,
        SortingOption.ORIGINAL_PRICE,
        SortingOption.DAYS_ON_MARKET,
        SortingOption.ALL_ROOMS_COUNT,
        SortingOption.BEDROOM_COUNT,
        SortingOption.FULL_BATHROOM_COUNT,
        SortingOption.STORIES,
        SortingOption.UNITS,
        SortingOption.GROSS_LIVING_AREA,
        SortingOption.YEAR_BUILT,
        SortingOption.LOT_SIZE
    ];

    // Derive sorted comparables from props and sorting state
    const sortedComparables = React.useMemo(() => {
        if (currentSortingMethod.label === SortingOption.RANKING) {
            return currentSortingMethod.descending ? [...selectableComps].reverse() : [...selectableComps];
        }
        if (sortNumerically.includes(currentSortingMethod.label)) {
            const list = [...selectableComps].sort((compA, compB) => {
                const paramA= compA.propertyValuation?.[currentSortingMethod.label] as number || 0;
                const paramB = compB.propertyValuation?.[currentSortingMethod.label] as number || 0;

                return paramA - paramB;
            });
            return currentSortingMethod.descending ? [...list].reverse(): [...list];
        }
        if (sortAlphabetically.includes(currentSortingMethod.label)) {
            const list = [...selectableComps].sort((compA, compB) => {
                const paramA = compA.propertyValuation?.[currentSortingMethod.label] as string || "";
                const paramB = compB.propertyValuation?.[currentSortingMethod.label] as string || "";

                return paramA.localeCompare(paramB);
            });
            return currentSortingMethod.descending ? [...list].reverse() : [...list];
        }

        return [...selectableComps];
    }, [selectableComps, currentSortingMethod]);

    const handleSortClick = useCallback((newMethodLabel: SortingOption) => {
        setCurrentSortingMethod(prev => ({
            label: newMethodLabel,
            descending: prev.label === newMethodLabel ? !prev.descending : false
        }));
    }, []);

    const handleClickSubject = useCallback(() => {
        const subjectSelectAbleComp: SelectableComp = {
            dgInfo: {
                dataGridFieldKey: "",
                prefixForApiPopulation: "",
                requiredType: CompSelectType.ANY
            },
            propertyValuation: subject?.comparable,
        }
        if (onCompClick) {
            onCompClick(subjectSelectAbleComp);
        }
    }, [subject]);

    return (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="flex justify-between px-4 py-3 lg:px-6 lg:py-4 bg-gray-800 text-white">
                <div className="flex-none">
                    <h2 className="text-lg lg:text-xl font-bold">Available Comparables</h2>
                    <p className="text-xs lg:text-sm text-gray-300 mt-0.5 lg:mt-1">
                        Drag properties to the selection area above
                    </p>
                </div>

                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsOpen(true);
                    }}
                    className={"flex-none right-0 items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition duration-150"}
                >
                    Add from MLS Search
                </button>

                {isOpen && (
                    <AddComparableModal
                        isOpen={isOpen}
                        onClose={() => setIsOpen(false)}
                        selectedComps={selectedComps}
                        setSelectedCompAtRow={setSelectedCompAtRow}
                    />
                )}
            </div>

            <div className="max-h-200 overflow-y-scroll">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="sticky top-0">
                        <tr>
                            {COLUMN_CONFIGS.map((column) => (
                                <th
                                    key={column.key}
                                    className={`bg-gray-200 px-1 sm:px-2 lg:px-3 py-1 lg:py-2 text-xs font-medium text-gray-700 uppercase ${getAlignmentClass(column.align)} ${column.minWidth || ''} ${column.className || ''}`}
                                >
                                    {column.header(handleSortClick, currentSortingMethod)}
                                </th>
                            ))}
                        </tr>
                        {subject && subject.comparable &&
                            <tr
                                className={"bg-gray-100 hover:bg-gray-50"}
                                onClick={handleClickSubject}
                            >
                                {/* Generate cells based on column configuration */}
                                {/* This ensures each cell corresponds to its header */}
                                {COLUMN_CONFIGS.map((column) => (
                                    <td
                                        key={column.key}
                                        className={`px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 ${getAlignmentClass(column.align)} ${column.minWidth || ''} ${column.className || ''}`}
                                    >
                                        {/* Render cell content using the column's render function */}
                                        {column.render(subject.comparable, true)}
                                    </td>
                                ))}
                            </tr>
                        }
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {sortedComparables.length === 0 ? (
                        <tr>
                            <td colSpan={COLUMN_CONFIGS.length} className="px-4 py-6 lg:py-8 text-center text-gray-500">
                                No comparable properties available
                            </td>
                        </tr>
                    ) : (
                        sortedComparables.map((selectable, index) => (
                                <DraggableCompRow
                                    key={`row-${index + 1}-active`}
                                    selectableComp={selectable}
                                    onCompClick={onCompClick}
                                    disabled={selectable.propertyValuation != undefined && selectable.propertyValuation.address != undefined && getSelectedCompByMlsIdOrAddress(selectable.propertyValuation.address, selectable.propertyValuation.mlsId) !== undefined}
                                />
                            )
                        )
                    )}
                    </tbody>
                </table>
            </div>
        </div>
    );
});
